import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Download, Upload, FileSpreadsheet, CheckCircle, AlertTriangle } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import { productsService } from '@/services/products';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type Result = { created: number; updated: number; unchanged?: number; errors: { row: number; error: string }[] };

export default function ProductImportModal({ isOpen, onClose }: Props) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDownloadTemplate = async () => {
    const blob = await productsService.downloadImportTemplate();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla-productos.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const r = await productsService.importExcel(file);
      setResult(r);
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudo importar el archivo.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setError(null);
    if (fileRef.current) fileRef.current.value = '';
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Importar productos desde Excel" size="lg">
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
          <p className="font-medium mb-1">Cómo funciona</p>
          <ul className="list-disc pl-5 space-y-0.5 text-blue-700">
            <li>Lo único <strong>obligatorio es el nombre</strong>. El resto es opcional.</li>
            <li>Columnas: <code>nombre, codigo, descripcion, categoria, proveedor, costo, precio, stock, unidad, iva</code>.</li>
            <li>Si <strong>re-subís</strong> el archivo, los productos que ya existen <strong>se actualizan</strong> (por código o nombre) — no se duplican.</li>
            <li>La <strong>categoría</strong> y el <strong>proveedor</strong> se crean solos si no existen. <code>unidad</code>: unidad / kg / g.</li>
          </ul>
        </div>

        <Button variant="secondary" onClick={handleDownloadTemplate}>
          <Download className="h-4 w-4 mr-2" />
          Descargar plantilla
        </Button>

        {/* Archivo */}
        <div
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-taupe-400"
        >
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); setError(null); }}
            className="hidden"
          />
          <FileSpreadsheet className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          {file ? (
            <p className="text-sm font-medium text-gray-800">{file.name}</p>
          ) : (
            <p className="text-sm text-gray-500">Hacé clic para elegir tu archivo .xlsx</p>
          )}
        </div>

        {error && <p className="text-sm text-rose-600">{error}</p>}

        {result && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
            <p className="flex items-center gap-2 text-emerald-800 font-medium">
              <CheckCircle className="h-4 w-4" />
              {result.created} creado(s) · {result.updated} actualizado(s)
              {result.unchanged ? ` · ${result.unchanged} sin cambios` : ''}
            </p>
            {result.errors.length > 0 && (
              <div className="mt-2 text-amber-800">
                <p className="flex items-center gap-1.5 font-medium"><AlertTriangle className="h-4 w-4" />{result.errors.length} fila(s) con problemas:</p>
                <ul className="list-disc pl-5 mt-1 max-h-32 overflow-y-auto">
                  {result.errors.slice(0, 30).map((er, i) => (
                    <li key={i}>Fila {er.row}: {er.error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={handleClose}>Cerrar</Button>
          <Button onClick={handleImport} loading={loading} disabled={!file}>
            <Upload className="h-4 w-4 mr-2" />
            Importar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
